import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

export default function Header() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 30);
    h(); window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/discover", label: t("nav.discover") },
    { to: "/map", label: t("nav.map") },
    { to: "/pourquoi", label: t("nav.why") },
  ];

  const dashboardPath = user?.role === "super_admin" ? "/super-admin" : user?.role === "cafe_owner" ? "/dashboard" : "/account";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-2xl bg-[#0A0908]/80 border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <Logo size={38} variant="mark" />
          <div className="leading-tight">
            <div className="font-serif text-[#F5F0EB] text-lg tracking-[0.18em]">
              <span className="text-[#C5A059]">CAFÉ-</span>SPOT
            </div>
            <div className="text-[9px] tracking-[0.32em] text-[#A8A39D] uppercase">Découvrez · Dégustez · Connectez</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace(/\//g, "-")}`}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-[13px] tracking-[0.15em] uppercase transition-colors duration-300 ${
                  isActive ? "text-[#C5A059]" : "text-[#A8A39D] hover:text-[#F5F0EB]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] text-[#A8A39D]">
            <button data-testid="lang-fr" onClick={() => setLang("fr")} className={lang === "fr" ? "text-[#C5A059]" : "hover:text-[#F5F0EB]"}>FR</button>
            <span>/</span>
            <button data-testid="lang-en" onClick={() => setLang("en")} className={lang === "en" ? "text-[#C5A059]" : "hover:text-[#F5F0EB]"}>EN</button>
          </div>
          {user ? (
            <>
              <Link to={dashboardPath} data-testid="header-dashboard" className="text-[13px] tracking-[0.15em] uppercase text-[#F5F0EB] hover:text-[#C5A059] transition-colors">
                {t("nav.dashboard")}
              </Link>
              <button data-testid="header-logout" onClick={() => { logout(); nav("/"); }} className="text-[13px] tracking-[0.15em] uppercase text-[#A8A39D] hover:text-[#F5F0EB] transition-colors">
                {t("nav.logout")}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login" className="text-[13px] tracking-[0.15em] uppercase text-[#A8A39D] hover:text-[#F5F0EB] transition-colors">
                {t("nav.login")}
              </Link>
              <Link to="/register" data-testid="header-register" className="btn-gold">
                {t("nav.register")}
              </Link>
            </>
          )}
        </div>

        <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden text-[#F5F0EB]">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-white/5 bg-[#0A0908]/95 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-[#F5F0EB] text-sm tracking-wider uppercase">
                {l.label}
              </Link>
            ))}
            <div className="border-t border-white/10 pt-4 flex flex-col gap-3">
              {user ? (
                <>
                  <Link to={dashboardPath} onClick={() => setOpen(false)} className="text-[#C5A059] text-sm tracking-wider uppercase">{t("nav.dashboard")}</Link>
                  <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="text-left text-[#A8A39D] text-sm tracking-wider uppercase">{t("nav.logout")}</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="text-[#A8A39D] text-sm tracking-wider uppercase">{t("nav.login")}</Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="text-[#C5A059] text-sm tracking-wider uppercase">{t("nav.register")}</Link>
                </>
              )}
              <div className="flex items-center gap-2 text-xs tracking-[0.2em] text-[#A8A39D] pt-2">
                <button onClick={() => setLang("fr")} className={lang === "fr" ? "text-[#C5A059]" : ""}>FR</button>
                <span>/</span>
                <button onClick={() => setLang("en")} className={lang === "en" ? "text-[#C5A059]" : ""}>EN</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
